import { createFileRoute, redirect } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";

/**
 * Short, brand-owned pay link: https://truwe.in/pay/<payment link id>
 *
 * WhatsApp templates can only carry a URL button on an approved TruWe host,
 * and we never want a raw provider URL in an approved template. This route
 * resolves the id to the live Razorpay link and redirects.
 *
 * Public by design (the id is an unguessable uuid), but it exposes nothing
 * about the order — only the hosted checkout URL, and only while the link
 * is still payable.
 */
const resolvePayLink = createServerFn({ method: "GET" })
  .inputValidator((data: { id: string }) => {
    const id = String(data?.id ?? "");
    if (!/^[0-9a-f-]{36}$/i.test(id)) throw new Error("bad_id");
    return { id };
  })
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { getLinkById } = await import("@/lib/payments/payment-links.server");
    const link = await getLinkById(supabaseAdmin, data.id);
    if (!link || !link.short_url) return { status: "not_found" as const, url: null };
    if (link.status === "paid") return { status: "paid" as const, url: null };
    if (link.status === "expired" || link.status === "cancelled") {
      return { status: "closed" as const, url: null };
    }
    if (link.expires_at && new Date(link.expires_at).getTime() <= Date.now()) {
      return { status: "closed" as const, url: null };
    }
    return { status: "ok" as const, url: link.short_url };
  });

export const Route = createFileRoute("/pay/$id")({
  loader: async ({ params }) => {
    const res = await resolvePayLink({ data: { id: params.id } }).catch(() => ({
      status: "not_found" as const,
      url: null,
    }));
    if (res.status === "ok" && res.url) throw redirect({ href: res.url });
    return res;
  },
  head: () => ({
    meta: [
      { title: "Complete your payment | TruWe" },
      {
        name: "description",
        content: "Securely complete the payment for your TruWe order.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: PayLinkFallback,
});

function PayLinkFallback() {
  const data = Route.useLoaderData();
  const message =
    data.status === "paid"
      ? "This payment has already been completed. Thank you!"
      : data.status === "closed"
        ? "This payment link has expired. Please contact us and we'll send a fresh one."
        : "We couldn't find this payment link.";

  return (
    <main className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center px-6 text-center">
      <h1 className="text-2xl font-semibold text-foreground">Payment link</h1>
      <p className="mt-3 text-sm text-muted-foreground">{message}</p>
      <a
        href="/"
        className="mt-8 rounded-full bg-foreground px-6 py-3 text-sm font-medium text-background"
      >
        Back to TruWe
      </a>
    </main>
  );
}
